import { lazy, Suspense, useEffect, useRef } from "react";
import GuestbookSection from "./GuestbookSection";

const MAP = {
  royal_indian: lazy(() => import("../templates/RoyalIndian")),
  floral_elegance: lazy(() => import("../templates/FloralElegance")),
  minimalist_modern: lazy(() => import("../templates/MinimalistModern")),
  traditional_mandala: lazy(() => import("../templates/TraditionalMandala")),
  beach_wedding: lazy(() => import("../templates/BeachWedding")),
  luxury_gold_black: lazy(() => import("../templates/LuxuryGoldBlack")),
  boho_chic: lazy(() => import("../templates/BohoChic")),
  vintage_romantic: lazy(() => import("../templates/VintageRomantic")),
  marigold_mela: lazy(() => import("../templates/MarigoldMela")),
  midnight_garden: lazy(() => import("../templates/MidnightGarden")),
  marathi_warli: lazy(() => import("../templates/MarathiWarli")),
  bengali_lotus: lazy(() => import("../templates/BengaliLotus")),
  christian_chapel: lazy(() => import("../templates/ChristianChapel")),
  punjabi_phulkari: lazy(() => import("../templates/PunjabiPhulkari")),
  teatro: lazy(() => import("../templates/Teatro")),
  day_night: lazy(() => import("../templates/DayNight")),
  nautical: lazy(() => import("../templates/Nautical")),
  aventureros: lazy(() => import("../templates/Aventureros")),
  editorial: lazy(() => import("../templates/Editorial")),
  rosas: lazy(() => import("../templates/Rosas")),
  mediterranean: lazy(() => import("../templates/Mediterranean")),
  dulce_amor: lazy(() => import("../templates/DulceAmor")),
  majestic: lazy(() => import("../templates/Majestic")),
};

const DEFAULT_PHOTOS = {
  groom: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2",
  bride: "https://images.unsplash.com/photo-1606216794074-735e91aa2c92",
  hero: "https://images.unsplash.com/photo-1519741497674-611481863552",
  cover: "https://images.unsplash.com/photo-1525258946800-98cfd641d0de",
  save: "https://images.unsplash.com/photo-1604017011826-d3b4c23f8914",
};

const BROKEN_PHOTOS = new Set([
  "1581181107603-7e9c1c50fb6c",
  "1521336575822-6da63fb45455",
  "1465495976277-4387d4b0e4a6",
  "1546961342-ae7a7c0d0e21",
  "1583089580344-3f9b1f97cebd",
  "1543159821-377b0d1c8b32",
  "1610890716171-6b1bb98ffd09",
]);

// Resize Unsplash images on-the-fly via their CDN params. This is the single biggest
// performance win for public invitations — we can drop a 2400px hero from ~2MB to ~80KB
// without any visual loss on a phone screen. Default quality 70, WebP format.
function optimize(url, w = 1200) {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("images.unsplash.com")) return url;
  const base = url.split("?")[0];
  return `${base}?w=${w}&q=70&auto=format&fm=webp&fit=crop`;
}

function fix(url, fallback, width = 1200) {
  if (!url) return optimize(fallback, width);
  for (const id of BROKEN_PHOTOS) if (url.includes(id)) return optimize(fallback, width);
  return optimize(url, width);
}

function normalize(data) {
  const d = { ...data };
  // Hero/cover images can use 1600px (full screen). Smaller portraits use 600-800px.
  d.groom_photo = fix(d.groom_photo, DEFAULT_PHOTOS.groom, 800);
  d.bride_photo = fix(d.bride_photo, DEFAULT_PHOTOS.bride, 800);
  d.hero_image = fix(d.hero_image, DEFAULT_PHOTOS.hero, 1600);
  d.cover_image = fix(d.cover_image, DEFAULT_PHOTOS.cover, 1400);
  d.save_the_date_image = fix(d.save_the_date_image, DEFAULT_PHOTOS.save, 1200);
  d.groom_full = d.groom_full || d.groom_name;
  d.bride_full = d.bride_full || d.bride_name;
  d.groom_parents = d.groom_parents || "";
  d.bride_parents = d.bride_parents || "";
  d.hashtag = d.hashtag || `#${(d.groom_name || "").replace(/\s/g, "")}Weds${(d.bride_name || "").replace(/\s/g, "")}`;
  d.music_url = d.music_url || "";
  if (!d.timeline || !Array.isArray(d.timeline) || d.timeline.length === 0) {
    d.timeline = [
      { date: "How we met", title: "Our First Hello", description: d.story || "Our story began with a simple smile.", image: fix(d.cover_image, DEFAULT_PHOTOS.cover, 800) },
    ];
  } else {
    d.timeline = d.timeline.map((t) => ({ ...t, image: fix(t.image, DEFAULT_PHOTOS.cover, 800) }));
  }
  const galleryFallbacks = [
    "https://images.unsplash.com/photo-1519741497674-611481863552",
    "https://images.unsplash.com/photo-1606216794074-735e91aa2c92",
    "https://images.unsplash.com/photo-1583939003579-730e3918a45a",
    "https://images.unsplash.com/photo-1525258946800-98cfd641d0de",
    "https://images.unsplash.com/photo-1519225421980-715cb0215aed",
    "https://images.unsplash.com/photo-1469371670807-013ccf25f16a",
    "https://images.unsplash.com/photo-1551316679-9c6ae9dec224",
    "https://images.unsplash.com/photo-1521336575822-6da63fb45455",
  ];
  d.gallery = (d.gallery && d.gallery.length ? d.gallery : galleryFallbacks).map((src, i) => fix(src, galleryFallbacks[i % galleryFallbacks.length], 1000));
  d.events = (d.events || []).map((e, i) => ({
    ...e,
    image: fix(e.image, d.gallery[i % d.gallery.length], 1000),
  }));
  return d;
}

const LoadingShimmer = () => (
  <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="template-loading">
    <div className="text-center">
      <div className="w-10 h-10 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin mx-auto" />
      <div className="mt-4 text-xs uppercase tracking-[0.3em] text-stone-500">Loading your invitation…</div>
    </div>
  </div>
);

export default function TemplateRenderer({ templateId, data, slug, isPreview = false }) {
  const Comp = MAP[templateId] || MAP.minimalist_modern;
  const safe = normalize(data || {});
  const accent = safe.palette?.accent || "#B89B5E";

  // After mount, decorate every image inside the template tree with lazy/async attrs.
  // This is the simplest way to apply `loading="lazy"` + `decoding="async"` across
  // all 23 templates without having to touch each .jsx file.
  const rootRef = useRef(null);
  useEffect(() => {
    if (!rootRef.current) return;
    const decorate = () => {
      const imgs = rootRef.current?.querySelectorAll("img") || [];
      imgs.forEach((img, i) => {
        if (!img.hasAttribute("loading")) img.setAttribute("loading", i < 2 ? "eager" : "lazy");
        if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
      });
    };
    // Initial pass + a delayed pass for lazy-rendered children
    decorate();
    const t1 = setTimeout(decorate, 600);
    const t2 = setTimeout(decorate, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [templateId, slug]);

  return (
    <div ref={rootRef} className="relative" data-testid={`template-render-${templateId}`}>
      <Suspense fallback={<LoadingShimmer />}>
        <Comp data={safe} slug={slug} isPreview={isPreview} />
        {(slug || isPreview) && safe.guestbook_enabled !== false && (
          <GuestbookSection slug={slug} accent={accent} isPreview={isPreview} />
        )}
      </Suspense>
      {isPreview && (
        <div className="fixed bottom-4 right-4 px-4 py-2 bg-black/80 text-white text-xs uppercase tracking-[0.3em] z-50">Preview</div>
      )}
    </div>
  );
}
